import type { MediaDescriptionContent3DX } from '@ds/swymkit/typings'

import { IMediaType } from '~/entities/content/media/interfaces/media-type'

import { IMediaType as IExaleadMediaType } from '~/features-shared/content/api/interfaces/params'
import { IContentModelExalead } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'

/**
 * Mapping all media subtypes to Exalead media subtypes
 */
const exaleadMediaTypeMap = new Map<IMediaType, IExaleadMediaType>([
  [IMediaType.AnimatedPicture, IExaleadMediaType.AnimatedPicture],
  [IMediaType.Document, IExaleadMediaType.Document],
  [IMediaType.Drawing, IExaleadMediaType.Drawing],
  [IMediaType.Model3D, IExaleadMediaType['3DModel']],
  [IMediaType.Picture, IExaleadMediaType.Picture],
  [IMediaType.Sketch, IExaleadMediaType.Sketch],
  [IMediaType.Sound, IExaleadMediaType.Sound],
  [IMediaType.Story, IExaleadMediaType.Story],
  [IMediaType.Video, IExaleadMediaType.Video],
  [IMediaType.Whiteboard, IExaleadMediaType.Whiteboard],
])

/**
 * Mapping all content types to exalead content types
 */
const exaleadContentTypeMap = new Map<IContentModel, IContentModelExalead>([
  [IContentModel.Conference, IContentModelExalead.Conference],
  [IContentModel.Idea, IContentModelExalead.Idea],
  [IContentModel.Post, IContentModelExalead.Post],
  [IContentModel.Question, IContentModelExalead.Iquestion],
  [IContentModel.Survey, IContentModelExalead.Survey],
  [IContentModel.Wedo, IContentModelExalead.Wedo],
  [IContentModel.Wiki, IContentModelExalead.Wiki],
  [IContentModel.Media, IContentModelExalead.Media],
])

/**
 * Get exalead media type
 * @param type Media type
 * @returns exalead media type
 */
export function getExaleadMediaObjectType(type: IMediaType): IExaleadMediaType | undefined {
  return exaleadMediaTypeMap.get(type)
}

/**
 * Get exalead content type
 * @param type Content type
 * @returns Exalead content type
 */
export function getExaleadContentObjectType(type: IContentModel): IContentModelExalead | undefined {
  return exaleadContentTypeMap.get(type)
}

/**
 * Get content type corresponding to 'ds6w:Type' 3dXContent dropped item
 * @param item - Dropped 3dXContent item
 * @returns contentType
 */
export function getType(item: MediaDescriptionContent3DX) {
  let _contentType
  // the supported contentType is like post, community, idea (when it comes from a dnd in the app 3DSwym).
  // when the drop comes from other apps (the search or 3Dplan),
  // it is the exalead pattern like swym:Post, swym:Community... In that case, the type must be extracted
  if (item.objectType && item.objectType.match('swym:'))
    _contentType = item.objectType.split('swym:')[1].toLowerCase()
  else if (item.objectType && item.objectType.indexOf(':') > 0)
    _contentType = item.objectType.split(':')[0]
  else
    _contentType = item.objectType

  switch (_contentType) {
    // 'conference' = 'social-event' in swym-v2
    case IContentModel.Conference:
      _contentType = 'social-event'
      break
    case IContentModel.Question:
      _contentType = 'question'
      break
    case 'wikitreepage':
      _contentType = IContentModel.Wiki // when this comes from exalead we have issues
      break
    default:
      // special case for media it can be swym:video ...
      // or swym:animatedPicture, no media is specified so we have to "look" inside the subjectURI (and we should not)
      if (!_contentType && item.objectId.includes(IContentModel.Media))
        _contentType = IContentModel.Media // Derived
  }
  return _contentType
}
